import React from 'react';
import StatsCard from '../../../shared/components/common/StatsCard';
import { formatPercentage } from '../../../utils/numberUtils';
import SecurityTrackingHelpModal from './SecurityTrackingHelpModal';

const THREAT_LABELS = {
  brute_force: { icon: '🔐', label: 'Brute Force' },
  credential_stuffing: { icon: '📋', label: 'Credential Stuffing' },
  unusual_locations: { icon: '📍', label: 'Unfamiliar IPs' },
  account_lockouts: { icon: '🔒', label: 'Lockouts' },
};

const toDateKey = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Fill a sparse API timeline into a fixed 7-day window (oldest → newest). */
const buildLast7DayTimeline = (rawTimeline = []) => {
  const byDate = new Map();
  rawTimeline.forEach(({ date, count }) => {
    const key = toDateKey(date);
    byDate.set(key, (byDate.get(key) || 0) + count);
  });

  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let offset = 6; offset >= 0; offset -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - offset);
    const key = toDateKey(d);
    days.push({ date: key, count: byDate.get(key) || 0 });
  }
  return days;
};

const getRiskCardClass = (level) => {
  if (level === 'high') return 'open alert-elevated';
  if (level === 'medium') return 'deactivated alert-elevated';
  return 'active';
};

export default function SecurityTrackingSection({
  securityMetrics,
  securityMetricsLoading,
  userManagement,
  showSecurityHelp,
  setShowSecurityHelp,
  onScrollToSection,
}) {
  const insights = securityMetrics?.security_insights;
  const anomalyCount = insights?.anomaly_count || 0;
  const failureRatio = insights?.failure_ratio || 0;
  const riskLevel = insights?.risk_level || 'low';
  const atRiskAccounts = insights?.at_risk_accounts || [];
  const threatTypes = insights?.threat_types || {};
  const recommendations = insights?.recommendations || [];
  const identityProbes = insights?.identity_probes
    || securityMetrics?.failed_logins?.identity_probes
    || { total_attempts: 0, unique_usernames: 0, top_usernames: [] };
  const timeline = buildLast7DayTimeline(securityMetrics?.failed_logins?.timeline || []);
  const timelineTotal = timeline.reduce((sum, day) => sum + day.count, 0);
  const timelineActiveDays = timeline.filter((day) => day.count > 0).length;
  const timelineMax = Math.max(...timeline.map((day) => day.count), 1);

  const activeThreats = Object.entries(threatTypes).filter(([, count]) => count > 0);

  const hasInsights = Boolean(
    securityMetrics?.security_insights
    || securityMetrics?.failed_logins
    || securityMetrics?.success_metrics
  );
  const hasSessionData = userManagement && (
    userManagement.active_sessions != null
    || userManagement.avg_sessions_per_user != null
    || userManagement.total_profile_updates != null
    || userManagement.unique_users_modified != null
  );

  const showRiskBanner = riskLevel === 'high' || riskLevel === 'medium' || anomalyCount > 0;

  return (
    <>
      <div className="list-default-window-note" role="note">
        <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
        <span>
          Location-scoped security insights for the <strong>last 7 days</strong>. Login failure
          details are in{' '}
          <button
            type="button"
            className="st-cross-link"
            onClick={() => onScrollToSection?.('user-management')}
          >
            User Management
          </button>
          ; device audit is in{' '}
          <button
            type="button"
            className="st-cross-link"
            onClick={() => onScrollToSection?.('audit-compliance')}
          >
            Audit &amp; Compliance
          </button>
          .
        </span>
      </div>

      {securityMetricsLoading && (
        <div className="user-mgmt-loading">Loading security metrics…</div>
      )}

      {hasInsights && (
        <div className="st-section">
          <div className="dashboard-subsection-label">Security insights</div>
          <div className="dashboard-stats-summary" style={{ marginBottom: '0.75rem' }}>
            <StatsCard
              icon="⚠️"
              number={anomalyCount}
              label="At-risk Accounts"
              className={`${anomalyCount > 0 ? 'open alert-elevated' : 'total'}`.trim()}
              title="Known accounts with high failure rate or volume in the last 7 days"
            />
            <StatsCard
              icon="⏱️"
              number={insights?.avg_session_duration || '—'}
              label="Avg Session"
              className="deactivated"
              title="Estimated average session duration"
            />
            <StatsCard
              icon="📊"
              number={formatPercentage(failureRatio)}
              label="Failure Rate"
              className={`${failureRatio > 10 ? 'open alert-elevated' : 'active'}`.trim()}
              title="Failed attempts as a share of all login attempts"
            />
            <StatsCard
              icon="🎯"
              number={(riskLevel || 'low').toUpperCase()}
              label="Risk Level"
              className={getRiskCardClass(riskLevel)}
              title="Overall security risk assessment"
            />
          </div>

          {showRiskBanner && (
            <div className="st-alert-banner">
              {riskLevel === 'high' && 'High risk — '}
              {riskLevel === 'medium' && 'Medium risk — '}
              {atRiskAccounts.length > 0
                ? `review ${atRiskAccounts.length} account${atRiskAccounts.length === 1 ? '' : 's'} at risk`
                : 'review authentication anomalies below'}
            </div>
          )}

          {recommendations.length > 0 && (
            <ul className="st-recommendations">
              {recommendations.map((rec, idx) => (
                <li key={idx}>{rec}</li>
              ))}
            </ul>
          )}

          {activeThreats.length > 0 && (
            <>
              <div className="dashboard-subsection-label">Threat categorization</div>
              <div className="st-threat-chips">
                {activeThreats.map(([key, count]) => {
                  const info = THREAT_LABELS[key] || { icon: '❓', label: key };
                  return (
                    <span key={key} className="st-threat-chip">
                      <span>{info.icon}</span>
                      <span className="st-threat-chip__count">{count}</span>
                      <span>{info.label}</span>
                    </span>
                  );
                })}
              </div>
            </>
          )}

          {(atRiskAccounts.length > 0 || identityProbes.total_attempts > 0 || timelineTotal > 0) && (
            <>
              <div className="dashboard-subsection-label">Authentication signals</div>
              <div className="st-signals">
                {timelineTotal > 0 && (
                  <div className="st-signals__block">
                    <div className="st-signals__heading">
                      Failed logins · last 7 days
                      <span className="st-signals__heading-meta">
                        {timelineTotal} total
                        {timelineActiveDays > 0
                          ? ` · ${timelineActiveDays} active day${timelineActiveDays === 1 ? '' : 's'}`
                          : ''}
                      </span>
                    </div>
                    <div className="st-timeline st-timeline--embedded" role="img" aria-label="Failed login counts by day">
                      {timeline.map((day) => {
                        const barHeightPx = day.count > 0
                          ? Math.max(8, Math.round((day.count / timelineMax) * 56))
                          : 0;
                        const isPeak = day.count > 0 && day.count === timelineMax;
                        const dateLabel = new Date(`${day.date}T12:00:00`).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        });
                        return (
                          <div key={day.date} className="st-timeline__day">
                            <div className="st-timeline__track">
                              <div
                                className={`st-timeline__bar${isPeak ? ' st-timeline__bar--peak' : ''}${day.count === 0 ? ' st-timeline__bar--zero' : ''}`}
                                style={{ height: `${barHeightPx}px` }}
                                title={`${dateLabel}: ${day.count} failure${day.count === 1 ? '' : 's'}`}
                                aria-label={`${dateLabel}: ${day.count} failures`}
                              >
                                {day.count > 0 && (
                                  <span className="st-timeline__count">{day.count}</span>
                                )}
                              </div>
                            </div>
                            <span className="st-timeline__label">{dateLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {atRiskAccounts.length > 0 && (
                  <div className="st-signals__block">
                    <div className="st-signals__heading">
                      Accounts at risk
                      <span className="st-signals__heading-meta">{atRiskAccounts.length}</span>
                    </div>
                    <ul className="st-signals-list">
                      {atRiskAccounts.map((account, idx) => (
                        <li
                          key={`${account.employee_id}-${idx}`}
                          className="st-signal-row st-signal-row--risk"
                        >
                          <span className="st-signal-row__main">
                            <strong>{account.employee_id || '—'}</strong>
                            <span className="st-signal-row__sep" aria-hidden="true">·</span>
                            <span>{account.full_name || '—'}</span>
                          </span>
                          <span className="st-signal-row__meta">
                            <span>{account.failures ?? 0} fail</span>
                            <span className="st-rate-badge st-rate-badge--bad">
                              {formatPercentage(account.failure_rate || 0)}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {identityProbes.total_attempts > 0 && identityProbes.top_usernames?.length > 0 && (
                  <div className="st-signals__block">
                    <div className="st-signals__heading">
                      Unrecognized usernames
                      <span className="st-signals__heading-meta">
                        {identityProbes.unique_usernames} unique · {identityProbes.total_attempts} attempts
                      </span>
                    </div>
                    <ul className="st-signals-list st-signals-list--scroll">
                      {identityProbes.top_usernames.map((probe) => (
                        <li key={probe.username} className="st-signal-row st-signal-row--probe">
                          <span className="st-signal-row__main st-signal-row__main--mono">
                            {probe.username}
                          </span>
                          <span className="st-signal-row__meta">{probe.attempts ?? 0}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="st-signals__footer">
                  <button
                    type="button"
                    className="st-signals-cta"
                    onClick={() => onScrollToSection?.('user-management')}
                  >
                    Open login health for full breakdown
                    <span aria-hidden="true">→</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {hasSessionData && (
        <div className="st-section">
          <div className="dashboard-subsection-label">Sessions &amp; profile activity</div>
          <div className="dashboard-stats-summary">
            <StatsCard
              icon="🖥️"
              number={userManagement.active_sessions ?? 0}
              label="Active Sessions"
              className="total"
              title="Browser sessions currently logged in"
            />
            <StatsCard
              icon="👥"
              number={userManagement.avg_sessions_per_user ?? 0}
              label="Avg / User"
              className="maintenance"
              title="Average sessions per active user"
            />
            <StatsCard
              icon="✏️"
              number={userManagement.total_profile_updates ?? 0}
              label="Profile Updates"
              className="calibration"
              title="Total profile change transactions"
            />
            <StatsCard
              icon="🔄"
              number={userManagement.unique_users_modified ?? 0}
              label="Users Modified"
              className="pending"
              title="Distinct users with profile changes"
            />
          </div>
        </div>
      )}

      {!hasInsights && !hasSessionData && !securityMetricsLoading && (
        <div className="user-mgmt-loading">No security tracking data available yet.</div>
      )}

      <SecurityTrackingHelpModal
        isOpen={showSecurityHelp}
        onClose={() => setShowSecurityHelp(false)}
      />
    </>
  );
}
